<template>
  <div class="gallery-experience-study">
    <section class="experience-collection" :aria-labelledby="headingId('design')">
      <header class="experience-heading">
        <component :is="groups.length > 1 ? 'h3' : 'h4'" :id="headingId('design')" class="experience-group-heading">{{ groups.length > 1 ? 'Designing the exhibition' : 'Gallery design' }}</component>
        <p v-if="groups.length > 1">{{ exhibitionDesignIntroduction }}</p>
        <p v-else>{{ galleryDesignIntroduction }}</p>
      </header>
      <nav class="experience-selector" aria-label="Design details">
        <div v-for="study in studies" :key="study.id" class="experience-feature-group">
          <span v-if="groups.length > 1" class="experience-category">{{ study.title }}</span>
          <button v-for="(feature, index) in study.features" :key="feature.id" type="button" :aria-pressed="active.id === feature.id" @click="selectedId = feature.id">
            <span class="experience-number">{{ String(index + 1).padStart(2, '0') }}</span><ExhibitionFeatureIcon :feature="feature.id" /><span>{{ feature.label }}</span>
          </button>
        </div>
      </nav>
      <ExhibitionDesignPicker class="experience-mobile-selector" :studies="studies" :value="active.id" @select="selectedId = $event" />
      <div class="experience-visual">
        <component :is="groups.length > 1 ? 'h4' : 'h5'" class="experience-active-title">{{ active.title }}</component>
        <div class="experience-screen">
          <GalleryStudyFilm v-if="active.video" :key="active.id" :src="film(active.video)" :poster="image(active.image)" :captions="active.sound ? film('audio', 'vtt') : undefined" :title="active.title" :label="active.caption" :sound="active.sound" />
          <figure v-else>
            <ExpandableProjectMedia :title="active.title" :aspect-ratio="1920/1046" :interactive="active.comparison || active.id === 'building'">
              <div v-if="active.id === 'building'" class="experience-building"><GalleryBuildingMark interactive :dark="galleryDark" /></div>
              <GalleryLightComparison v-else-if="active.comparison" :light="themedImage(active.image, false)" :dark="themedImage(active.image, true)" :alt="active.alt" />
              <div v-else class="experience-image">
                <img :src="image(active.image, active.themed)" :alt="active.alt" width="1920" height="1046" loading="lazy" decoding="async" />
                <svg v-if="active.id === 'rooms'" class="rooms-focus-mask" viewBox="0 0 1920 1046" aria-hidden="true">
                  <defs><mask :id="maskId"><rect width="1920" height="1046" fill="white"/><rect x="32" y="132" width="489" height="728" rx="2" fill="black"/></mask></defs>
                  <rect width="1920" height="1046" fill="#171610" fill-opacity=".48" :mask="`url(#${maskId})`"/>
                </svg>
              </div>
              <template #caption>
                <figcaption>
                  <span>{{ active.id === 'building' ? 'Drag to turn the building' : active.caption }}</span>
                  <div v-if="active.themed" class="experience-controls">
                  <button class="experience-theme" type="button" :aria-label="galleryDark ? 'Use light theme for Gallery images' : 'Use dark theme for Gallery images'" :aria-pressed="galleryDark" @click="themeOverride = !galleryDark">
                    <svg viewBox="0 0 20 20" aria-hidden="true"><template v-if="galleryDark"><circle cx="10" cy="10" r="3.5"/><path d="M10 1v2m0 14v2M1 10h2m14 0h2M3.6 3.6 5 5m10 10 1.4 1.4M3.6 16.4 5 15M15 5l1.4-1.4"/></template><path v-else d="M16.8 12.4A7 7 0 0 1 7.6 3.2a7 7 0 1 0 9.2 9.2Z"/></svg>
                  </button>
                  </div>
                </figcaption>
              </template>
            </ExpandableProjectMedia>
          </figure>
        </div>
        <div class="experience-descriptions" aria-live="polite" aria-atomic="true"><p>{{ active.copy }}</p></div>
        <details v-if="active.mechanism" :key="active.id" class="experience-mechanism">
          <summary>{{ active.mechanism.title }}<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10"/><path class="disclosure-vertical" d="M8 3v10"/></svg></summary>
          <div class="mechanism-copy"><p v-for="paragraph in active.mechanism.paragraphs" :key="paragraph">{{ paragraph }}</p></div>
        </details>
      </div>
    </section>
  </div>
</template>
<script>
import GalleryBuildingMark from './GalleryBuildingMark.vue';
import GalleryStudyFilm from './GalleryStudyFilm.vue';
import GalleryLightComparison from './GalleryLightComparison.vue';
import ExpandableProjectMedia from './ExpandableProjectMedia.vue';
import ExhibitionFeatureIcon from './ExhibitionFeatureIcon.vue';
import ExhibitionDesignPicker from './ExhibitionDesignPicker.vue';
import { initialExhibitionDarkMode } from '../../utils/exhibitionTheme';

import { galleryDesignIntroduction, exhibitionDesignIntroduction, galleryStudyFeatures as features, galleryStudyCollections as collections } from '../../data/galleryDesignStudies';

export default {
  components: { GalleryBuildingMark, GalleryStudyFilm, GalleryLightComparison, ExpandableProjectMedia, ExhibitionFeatureIcon, ExhibitionDesignPicker },
  props: { groups: { type: Array, default: () => ['gallery', 'exhibition', 'atmosphere'] } },
  data: () => ({ selectedId: '', dark: initialExhibitionDarkMode(), themeOverride: null }),
  computed: {
    galleryDesignIntroduction() { return galleryDesignIntroduction; },
    exhibitionDesignIntroduction() { return exhibitionDesignIntroduction; },
    active() { const items = this.studies.flatMap(study => study.features); return items.find(item => item.id === this.selectedId) || items[0]; },
    maskId() { return `rooms-study-mask-${this.$.uid}`; },
    galleryDark() { return this.themeOverride ?? this.dark; },
    studies() {
      return collections.filter(collection => this.groups.includes(collection.id)).map(collection => {
        const items = collection.ids.map(id => features.find(feature => feature.id === id));
        return { ...collection, features: items };
      });
    },
  },
  mounted() {
    this.syncTheme(); window.addEventListener('theme-change', this.syncTheme);
    this._imageObserver = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      for (const feature of features.filter(feature => feature.themed || feature.comparison)) for (const dark of [false, true]) {
        const image = new Image(); image.src = this.themedImage(feature.image, dark); image.decode().catch(() => {});
      }
      this._imageObserver.disconnect();
    }, {rootMargin:'300px'});
    this._imageObserver.observe(this.$el.querySelector('.experience-collection'));
  },
  beforeUnmount() { window.removeEventListener('theme-change', this.syncTheme); this._imageObserver?.disconnect(); },
  methods: {
    syncTheme() { this.dark = initialExhibitionDarkMode(); },
    headingId(id) { return `experience-${this.$.uid}-${id}`; },
    themedImage(name, dark) { return `/assets/img/projects/gallery-experience/${name}-${dark ? 'dark' : 'light'}.webp?v=20260925`; },
    image(name, themed = false) { return `/assets/img/projects/gallery-experience/${name}${themed ? `-${this.galleryDark ? 'dark' : 'light'}` : ''}.webp?v=${['arrival', 'wall-writing', 'globe', 'rooms', 'afterword'].includes(name) ? '20260927-3' : '20260925'}`; },
    film(name, extension = 'mp4') { return `/assets/video/gallery-experience/${name}.${extension}${name === 'translation' ? '' : name === 'afterword' ? '?v=20260921-2' : '?v=20260920'}`; },
  },
};
</script>
<style scoped>
.gallery-experience-study { margin-top: clamp(2.5rem,4vw,4rem); }
 .experience-group-heading { margin:0 0 1rem;font:400 clamp(1.28rem,1.75vw,1.55rem)/1.3 'PT Serif',Georgia,serif;color:var(--case-ink); }
.experience-feature-group { display:grid;gap:0; }
.experience-feature-group + .experience-feature-group { margin-top:1.3rem; }
.experience-category { margin:0 12px 12px;color:var(--case-muted);font:12px/1.4 'Quattrocento Sans',sans-serif; }
.experience-collection { display:grid;grid-template-columns:210px minmax(0,1fr);gap:1.8rem 3rem;align-items:start; }
.experience-collection + .experience-collection { margin-top:clamp(3rem,5vw,5rem); }
.experience-heading { grid-column:1/-1;max-width:52rem; }

.experience-heading p { margin:0; }
.experience-selector { display:grid;gap:8px; }
.experience-selector button { display:flex;align-items:center;gap:10px;min-height:48px;margin:0;border:0;padding:12.5px 12px;background:transparent;color:var(--case-muted);font:14px/1.35 'Quattrocento Sans',sans-serif;text-align:left;cursor:var(--site-interactive-cursor,pointer);border-radius:3px;transition:background-color 60ms linear,color 60ms linear; }
.experience-selector button[aria-pressed=true] { background:color-mix(in srgb,var(--case-ink) 6%,transparent);color:var(--case-ink); }
.experience-selector button { user-select:none; }
.experience-selector button:hover { color:var(--case-ink); }
.experience-number { font-size:10px;opacity:.6; }
.experience-selector :deep(svg) { width:22px;height:22px;flex:none; }
.experience-mobile-selector { display:none; }
.experience-visual { min-width:0; }
.experience-active-title { margin:0 0 1rem;font:400 1.15rem/1.35 'PT Serif',Georgia,serif;color:var(--case-ink); }
.experience-descriptions { margin-top:1rem;max-width:54rem; }
.experience-descriptions p { margin:0; }
.experience-screen, figure { margin: 0; min-width: 0; }
.experience-building{width:100%;aspect-ratio:1920/1046;background:color-mix(in srgb,var(--case-ink) 3%,transparent)}
.experience-image { position: relative; }
img { display: block; width: 100%; height: auto; aspect-ratio: 1920/1046; }
figcaption { display: flex; justify-content: space-between; align-items: center; gap: 1rem; min-height: 2.6rem; font: 13px/1.4 'Quattrocento Sans', sans-serif; color: var(--case-muted); }
.experience-controls { display: flex; align-items: center; gap: .55rem; }
.experience-controls button { display: inline-flex; align-items: center; justify-content: center; gap: .4rem; min-height: 36px; padding: 6px 0; border: 0; background: none; color: inherit; font: inherit; white-space: nowrap; cursor: var(--site-interactive-cursor, pointer); transition: opacity 160ms ease, transform 160ms ease; }
.experience-controls .experience-theme { width: 36px; padding: 9px; }
.experience-controls svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 1.2; stroke-linecap: round; stroke-linejoin: round; }
h4 { margin: 0; font: 400 clamp(1.05rem, 1.4vw, 1.25rem)/1.3 'PT Serif', Georgia, serif; letter-spacing: -.012em; color: var(--case-ink); }
p { margin: 0 0 .85em; font: inherit; font-size: 1.05rem; line-height: 1.8; color: var(--case-muted); }
p:last-child { margin-bottom: 0; }
.experience-mechanism { margin-top: .5rem; font-size: .9em; }
summary { display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 1rem 0; list-style: none; cursor: var(--site-interactive-cursor, pointer); color: var(--case-ink); }
summary::-webkit-details-marker { display: none; }
summary svg { width: 16px; height: 16px; fill: none; stroke: currentColor; stroke-width: 1.2; flex-shrink: 0; }
details[open] .disclosure-vertical { display: none; }
.mechanism-copy p { font-size: inherit; }
.mechanism-copy { max-width: 65ch; padding: 0 0 .75rem; }
.rooms-focus-mask { position: absolute; inset: 0; width: 100%; height: auto; pointer-events: none; }
button:focus-visible, summary:focus-visible { outline: 1px solid currentColor; outline-offset: 4px; }
.experience-controls button:active { transform: scale(.96); }
@media (hover:hover) and (pointer:fine) {
 .experience-selector button:hover { transition-duration:0ms; }
 .experience-selector button:hover:not([aria-pressed=true]) { background:color-mix(in srgb,var(--case-ink) 3%,transparent); }
 .experience-controls button:hover { opacity:.65; }
}
@media (max-width:900px) {
 .experience-collection { grid-template-columns:170px minmax(0,1fr);column-gap:1.5rem; }
}
@media (max-width:760px) {
 .experience-collection { display:flex;flex-direction:column;gap:1.2rem; }
 .experience-selector { display:none; }
 .experience-mobile-selector { position:relative;display:block;width:100%; }
 .experience-active-title { font-size:1.05rem;margin-bottom:.8rem; }
 .experience-visual { width:100%; }

 p { font-size:1rem;line-height:1.75; }
 figcaption { gap:.5rem;padding:.35rem 0; }
}
@media (prefers-reduced-motion:reduce) { button { transition:none!important; } }
</style>
